import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import { yupResolver } from '@hookform/resolvers/yup'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import debounce from '@mui/material/utils/debounce'
import unionBy from 'lodash/unionBy'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import * as Yup from 'yup'

import EditLandingPageLabels from '@/components/EditLandingPageLabels'
import TextEditor from '@/shared/TextEditor'

const mapStateToProps = ({ loading }) => ({
  isLandingPageLoading: loading['landingpage/getAddableLandingPages'],
})

const inputConfig = {
  variant: 'standard',
  inputBaseProps: {
    labelSuffix: '',
    flexDirection: 'column',
    alignItems: 'stretch',
    spacing: 0,
  },
  endAdornment: args => {
    const { value, inputProps } = args

    const { maxLength } = inputProps || {}

    if (maxLength) {
      return (
        <Typography
          variant="caption"
          sx={{ color: '#00000040', fontSize: '14px !important' }}
        >{`${value.length}/${maxLength}`}</Typography>
      )
    }
    return null
  },
}

const templateOptions = [
  {
    title: 'Login Form',
    description: 'Sample login form',
    content:
      '<form action="{{landing_page_url}}" method="post"><input id="username" style="width: 300px; font-size: 14px; border-radius: 2px; padding: 12px 20px; margin: 8px 0; display: inline-block; border: 1px solid #ccc; box-sizing: border-box;" autocomplete="off" name="username" required="" type="text" placeholder="Email Address" data-mce-style="width: 300px; font-size: 14px; border-radius: 2px; padding: 12px 20px; margin: 8px 0; display: inline-block; border: 1px solid #ccc; box-sizing: border-box;"><br><input id="password" style="width: 300px; font-size: 14px; border-radius: 2px; padding: 12px 20px; margin: 8px 0; display: inline-block; border: 1px solid #ccc; box-sizing: border-box;" autocomplete="off" name="password" required="" type="password" placeholder="Password" data-mce-style="width: 300px; font-size: 14px; border-radius: 2px; padding: 12px 20px; margin: 8px 0; display: inline-block; border: 1px solid #ccc; box-sizing: border-box;"><br><button style="width: 300px; font-size: 14px; border-radius: 2px; background-color: #394edc; border: none; color: white; padding-top: 10px; padding-bottom: 10px; text-decoration: none; margin: 4px 0; cursor: pointer;" type="submit" data-mce-style="width: 300px; font-size: 14px; border-radius: 2px; background-color: #394edc; border: none; color: white; padding-top: 10px; padding-bottom: 10px; text-decoration: none; margin: 4px 0; cursor: pointer;">Login</button></form>',
  },
]

const LandingPageMobileTemplateForm = ({
  dispatch,
  data,
  onSubmit: propsOnSubmit,
  isLandingPageLoading,
  type,
}) => {
  const { t } = useTranslation()

  const isSystem = type === 'system'

  const [restoreContent, setRestoreContent] = useState('')
  const [showLabels, setShowLabels] = useState(false)
  const [selectedLabels, setSelectedLabels] = useState([])
  const [editorError, setEditorError] = useState()

  Yup.addMethod(Yup.object, 'landingPageSelect', function testLandingPageSelect(errorMessage) {
    return this.test('test-landing-page-select', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context

      const { isLandingPage, landingPage } = parent

      if (isLandingPage && !landingPage) {
        return createError({ path, message: errorMessage })
      }

      return true
    })
  })

  const searchInputRef = useRef()
  const textEditorRef = useRef()

  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState({})
  const [page, setPage] = useState(0)

  const [forceLandingPage, setForceLandingPage] = useState(false)

  const { data: searchResultsData = [], paging } = searchResults

  const fetchData = useCallback(() => {
    dispatch({
      type: 'landingPage/getAddableLandingPages',
      payload: {
        pageIndex: page,
        pageSize: 100,
        searchName: searchInputRef.current,
        type: isSystem ? 'S' : 'I',
      },
      successCallback: response => {
        const { data: responseData, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.landingpageTempId, label: d.name }))

        setSearchResults(s => ({
          data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, isSystem, page])

  const loadMoreDebounce = debounce(() => {
    setPage(p => p + 1)
  }, 400)

  const fetchDataDebounce = useMemo(
    () =>
      debounce(() => {
        fetchData()
      }, 400),
    [fetchData],
  )

  useEffect(() => {
    fetchDataDebounce()
  }, [searchInput, page, fetchDataDebounce])

  const validations = {
    landingPage: Yup.object()
      .landingPageSelect(t('validation.landing_page_required'))
      .typeError(t('validation.landing_page_required'))
      .nullable(),
    name: Yup.string().required(t('validation.template_name_required')),
    htmlTitle: Yup.string().required(t('validation.html_title_required')),
  }

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    // eslint-disable-next-line no-unused-vars
    formState: { errors },
    reset: formReset,
  } = useForm({
    resolver: yupResolver(Yup.object().shape(validations)),
  })

  useEffect(() => {
    if (data) {
      const {
        isRefLandingPage: formIsLandingPage,
        refLandingpageId: formLandingPage,
        name: formName,
        htmlTitle: formSubject,
        content: formContent,
        labelNameList: formLabelList,
        refLandingpageName,
      } = data

      if (formIsLandingPage !== null) {
        setValue('isLandingPage', formIsLandingPage)
        if (formLandingPage && refLandingpageName) {
          setValue('landingPage', { id: formLandingPage, label: refLandingpageName })
        }
      }

      if (formName !== null) {
        setValue('name', formName)
      }

      if (formSubject !== null) {
        setValue('htmlTitle', formSubject)
      }

      if (formContent !== null) {
        setRestoreContent(formContent)
      }

      if (formLabelList !== null) {
        setSelectedLabels(formLabelList || [])
      }

      const hasLandingPage = formContent?.includes('{{landing_page_url}}')
      setForceLandingPage(hasLandingPage)

      if (hasLandingPage) {
        setValue('isLandingPage', true)
      }
    }
  }, [data, setValue])

  useEffect(() => {
    if (selectedLabels) {
      setValue('labels', selectedLabels.map(({ label }) => label).join(','))
    }
    trigger?.('labels')
  }, [setValue, selectedLabels, trigger])

  const onSubmit = values => {
    const { name, htmlTitle, isLandingPage: isRefLandingPage, landingPage } = values

    const editorContent = textEditorRef.current.editor().getContent()

    const hasLandingPage = editorContent?.includes('{{landing_page_url}}')

    const linkMissing = isRefLandingPage && !hasLandingPage

    setEditorError(linkMissing ? t('validation.landing_page_url_required') : null)

    if (!linkMissing) {
      const payload = {
        name,
        // NOTE: backend received label array instead of id array
        labelNameList: selectedLabels.map(({ label }) => label),
        htmlTitle,
        isRefLandingPage,
        htmlContent: textEditorRef.current.editor().getContent(),
        gcpStorageUrlList: textEditorRef.current.urlList(),
        type: isSystem ? 'SYSTEM' : 'INDIVIDUAL',
      }

      if (isRefLandingPage) {
        payload.refLandingpageId = landingPage.id
      }

      propsOnSubmit?.(payload)
    }

    formReset?.({}, { keepValues: true })
  }

  const isLandingPage = watch('isLandingPage')
  const landingPage = watch('landingPage')

  return (
    <Container maxWidth={false}>
      <Card>
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit, () => {
              trigger?.()
            })}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.template_name')}
                  defaultValue=""
                  name="name"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 80 } }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.label')}
                  defaultValue=""
                  name="labels"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    InputProps: { readOnly: true },
                    onClick: () => {
                      setShowLabels(true)
                    },
                  }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.phish_website_name')}
                  defaultValue=""
                  name="htmlTitle"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 100 } }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12}>
                <TextEditor
                  isMobile
                  ref={textEditorRef}
                  error={editorError}
                  onInit={editor => {
                    setTimeout(() => {
                      if (data?.content) {
                        editor.setContent(data.content)
                      }
                    }, 0)
                  }}
                  onContentChange={content => {
                    const hasLandingPage = content?.includes('{{landing_page_url}}')
                    setForceLandingPage(hasLandingPage)

                    if (hasLandingPage) {
                      setValue('isLandingPage', true)
                    }
                  }}
                  restoreContent={restoreContent}
                  templateOptions={templateOptions}
                  imageUploadCallback={(file, cb, errCb) => {
                    const formData = new FormData()
                    formData.append('file', file)

                    dispatch({
                      type: 'landingPage/uploadLandingPageImage',
                      payload: formData,
                      successCallback: res => {
                        cb(res)
                      },
                      errorCallback: err => {
                        errCb(err)
                      },
                    })
                  }}
                />
              </Grid>

              <Grid container item xs={12}>
                <Grid item>
                  <FormItem
                    label={t('general.select_landing_page')}
                    defaultValue={false}
                    name="isLandingPage"
                    disabled={forceLandingPage}
                    type="checkbox"
                    control={control}
                    inputConfig={{
                      ...inputConfig,
                    }}
                    errors={errors}
                  />
                </Grid>

                {isLandingPage && (
                  <Grid item>
                    <Stack direction="row" spacing={2}>
                      <FormItem
                        name="landingPage"
                        type="autocomplete"
                        control={control}
                        errors={errors}
                        inputConfig={{
                          ...inputConfig,
                          sx: { width: 200, mt: 1 },
                          onInputChange: (_, value, reason) => {
                            searchInputRef.current = value

                            if (['input', 'clear'].includes(reason)) {
                              setPage(0)
                              setSearchInput(value)
                            }
                          },
                          onFocus: () => {
                            fetchDataDebounce()
                          },
                          onLoadMore: () => {
                            const { hasNext } = paging

                            if (!isLandingPageLoading && hasNext) {
                              loadMoreDebounce()
                            }
                          },
                          options: searchResultsData,
                          filterOptions: opts => opts,
                          isOptionEqualToValue: (option, value) => option.id === value.id,
                          renderOption: (renderProps, option) => (
                            <Box component="li" {...renderProps} key={option.id}>
                              {option.label}
                            </Box>
                          ),
                        }}
                      />

                      <Button
                        disabled={!landingPage}
                        onClick={() => {
                          dispatch({
                            type: 'landingPage/getLandingPageHtml',
                            payload: { landingpageTempId: landingPage.id },
                            successCallback: html => {
                              const w = window.open('', '_blank', '') // access the "about:blank" window you've opened
                              w.document.body.innerHTML = html
                            },
                          })
                        }}
                      >
                        {t('general.preview')}
                      </Button>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  onClick={() => {
                    const w = window.open('', '_blank', '') // access the "about:blank" window you've opened
                    w.document.body.innerHTML = textEditorRef.current.editor().getContent()
                  }}
                >
                  {t('general.preview')}
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit">{t('general.complete')}</Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {showLabels && (
        <EditLandingPageLabels
          isEdit={!!data}
          landingpageTempId={data?.landingpageTempId}
          open={showLabels}
          data={selectedLabels}
          onClose={() => {
            setShowLabels(false)
          }}
          onComplete={setSelectedLabels}
          type={type}
        />
      )}
    </Container>
  )
}

export default connect(mapStateToProps)(LandingPageMobileTemplateForm)
